import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { TableSkeletonRows } from "../components/ui/Skeleton";
import {
  importApi,
  ImportHistoryDto,
  ImportResultDto,
  ImportValidationResultDto,
} from "../api/importApi";

const ImportPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [crcFormat, setCrcFormat] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ImportValidationResultDto | null>(null);
  const [importResult, setImportResult] = useState<ImportResultDto | null>(null);
  const [history, setHistory] = useState<ImportHistoryDto[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const h = await importApi.history();
      setHistory(h);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const pickFile = (f: File | null) => {
    setFile(f);
    setValidation(null);
    setImportResult(null);
    setError(null);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && (f.name.endsWith(".csv") || f.type === "text/csv" || f.type === "application/vnd.ms-excel")) {
      pickFile(f);
    } else {
      setError("Please drop a .csv file.");
    }
  };

  const runValidate = async () => {
    if (!file) {
      setError("Choose a CSV file first.");
      return;
    }
    setValidating(true);
    setError(null);
    setImportResult(null);
    try {
      const v = await importApi.validate(file, crcFormat);
      setValidation(v);
    } catch {
      setError("Validation failed. Check the file and try again.");
    } finally {
      setValidating(false);
    }
  };

  const runImport = async () => {
    if (!file) {
      setError("Choose a CSV file first.");
      return;
    }
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const r = crcFormat ? await importApi.importCreditRepairCloud(file) : await importApi.importCsv(file);
      setImportResult(r);
      await loadHistory();
    } catch {
      setError("Import failed. Validate first or check your connection.");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    setError(null);
    try {
      await importApi.downloadTemplate();
    } catch {
      setError("Could not download template.");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Import clients</h1>
          <p className="mt-1 text-sm text-slate-400">
            Upload a CSV in IntelliWarrior format, or use Credit Repair Cloud export column mapping.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm text-red-200">{error}</div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <button
            type="button"
            onClick={() => void downloadTemplate()}
            className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700"
          >
            Download CSV template
          </button>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={crcFormat}
              onChange={(e) => {
                setCrcFormat(e.target.checked);
                setValidation(null);
                setImportResult(null);
              }}
              className="rounded border-slate-600 bg-slate-800"
            />
            Credit Repair Cloud export format
          </label>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-xl border-2 border-dashed px-6 py-14 text-center transition ${
            dragOver ? "border-blue-500 bg-blue-950/20" : "border-slate-700 bg-slate-900/50"
          }`}
        >
          <p className="text-sm font-medium text-slate-300">Drag and drop your CSV here</p>
          <p className="mt-2 text-xs text-slate-500">or</p>
          <label className="mt-4 inline-block cursor-pointer rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">
            Browse files
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {file && (
            <p className="mt-4 text-sm text-emerald-400">
              Selected: <span className="font-mono text-white">{file.name}</span> ({Math.round(file.size / 1024)} KB)
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
          <button
            type="button"
            disabled={!file || validating}
            onClick={() => void runValidate()}
            className="w-full rounded-lg bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-600 disabled:opacity-50 sm:w-auto"
          >
            {validating ? "Validating…" : "Validate before import"}
          </button>
          <button
            type="button"
            disabled={!file || importing}
            onClick={() => void runImport()}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 sm:w-auto"
          >
            {importing ? "Importing…" : "Import"}
          </button>
        </div>

        {validation && (
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            <h2 className="text-lg font-semibold text-white">Validation</h2>
            <p className="mt-1 text-sm text-slate-400">
              {validation.fileName} — {validation.totalRows} row(s),{" "}
              <span className="text-emerald-400">{validation.validRows} valid</span>,{" "}
              <span className="text-red-400">{validation.errorRows} with errors</span>
              {validation.creditRepairCloudFormat && (
                <span className="ml-2 rounded bg-amber-950/60 px-2 py-0.5 text-xs text-amber-200">CRC mapping</span>
              )}
            </p>

            {validation.errors.length > 0 && (
              <div className="mt-4 max-h-48 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Errors</p>
                <ul className="mt-2 space-y-1 text-sm text-red-300">
                  {validation.errors.slice(0, 50).map((e, i) => (
                    <li key={`${e.row}-${i}`}>
                      Row {e.row}: {e.message}
                    </li>
                  ))}
                  {validation.errors.length > 50 && (
                    <li className="text-slate-500">…and {validation.errors.length - 50} more</li>
                  )}
                </ul>
              </div>
            )}

            {validation.previewRows.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <p className="text-xs font-semibold uppercase text-slate-500">Row preview</p>
                <table className="mt-2 w-full min-w-[720px] text-left text-xs text-slate-300">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500">
                      <th className="py-2 pr-2">Row</th>
                      <th className="py-2 pr-2">Status</th>
                      <th className="py-2 pr-2">Name</th>
                      <th className="py-2 pr-2">Email</th>
                      <th className="py-2 pr-2">DOB</th>
                      <th className="py-2 pr-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.previewRows.map((p) => (
                      <tr key={p.row} className="border-b border-slate-800/60">
                        <td className="py-2 pr-2 font-mono">{p.row}</td>
                        <td className="py-2 pr-2">
                          <span
                            className={
                              p.isValid
                                ? "rounded bg-emerald-950/60 px-2 py-0.5 text-emerald-200"
                                : "rounded bg-red-950/50 px-2 py-0.5 text-red-200"
                            }
                          >
                            {p.isValid ? "Valid" : "Error"}
                          </span>
                        </td>
                        <td className="py-2 pr-2">
                          {p.firstName} {p.lastName}
                        </td>
                        <td className="py-2 pr-2">{p.email}</td>
                        <td className="py-2 pr-2">{p.dateOfBirth || "—"}</td>
                        <td className="max-w-[220px] py-2 pr-2 text-red-300/90">
                          {p.messages?.length ? p.messages.join(" · ") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {importResult && (
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            <h2 className="text-lg font-semibold text-white">Import result</h2>
            <p className="mt-2 text-sm text-slate-300">
              Status: <span className="font-medium text-white">{importResult.status}</span> — imported{" "}
              <span className="text-emerald-400">{importResult.successCount}</span>, errors{" "}
              <span className="text-red-400">{importResult.errorCount}</span> (of {importResult.totalRows} rows)
            </p>
            {importResult.errors.length > 0 && (
              <ul className="mt-3 max-h-36 space-y-1 overflow-y-auto text-sm text-red-300">
                {importResult.errors.map((e, i) => (
                  <li key={`${e.row}-${i}`}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="border-b border-slate-800 px-5 py-4">
            <h2 className="text-lg font-semibold text-white">Import history</h2>
            <p className="text-sm text-slate-500">Recent runs for your workspace.</p>
          </div>
          <div className="overflow-x-auto">
            {historyLoading ? (
              <div className="px-5 py-6">
                <TableSkeletonRows rows={5} cols={6} />
              </div>
            ) : history.length === 0 ? (
              <p className="px-5 py-8 text-sm text-slate-500">No imports yet.</p>
            ) : (
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="px-5 py-3 font-medium">When</th>
                    <th className="px-5 py-3 font-medium">File</th>
                    <th className="px-5 py-3 font-medium">Rows</th>
                    <th className="px-5 py-3 font-medium">OK</th>
                    <th className="px-5 py-3 font-medium">Errors</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80">
                  {history.map((h) => (
                    <tr key={h.id} className="text-slate-300">
                      <td className="px-5 py-3 text-xs text-slate-500">
                        {new Date(h.createdAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs">{h.fileName}</td>
                      <td className="px-5 py-3">{h.totalRows}</td>
                      <td className="px-5 py-3 text-emerald-400">{h.successCount}</td>
                      <td className="px-5 py-3 text-red-400">{h.errorCount}</td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs">{h.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default ImportPage;
